import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth, type MemberRole, type MemberStatus } from '../auth/AuthProvider'
import { authHeaders, fn, supabase } from '../auth/supabase'
import { formatDate } from '../lib/date'

interface MemberRow {
  id: string
  auth_user_id: string | null
  player_id: string | null
  display_name: string | null
  email: string | null
  role: MemberRole
  status: MemberStatus
  claimed_at: string
  approved_at: string | null
  last_sign_in_at: string | null
  device_count: number
  created_by_admin: boolean
}

interface RosterName {
  id: string
  display_name: string
}

type Note = { kind: 'ok' | 'error'; text: string } | null

/**
 * Approving teammates, and adding them directly, from a phone.
 *
 * Authority is the signed-in admin's own role, not the shared password: they are
 * already authenticated, and every reversible action writes straight to Postgres
 * under an RLS policy. The two things RLS cannot do -- read the auth schema for a
 * last-sign-in time, and create an account -- go through admin-gated functions
 * that check the same role server-side.
 */
export function Admin() {
  const { session } = useAuth()
  const [members, setMembers] = useState<MemberRow[] | null>(null)
  const [roster, setRoster] = useState<RosterName[]>([])
  const [note, setNote] = useState<Note>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [checkedAt, setCheckedAt] = useState<number | null>(null)
  const [checking, setChecking] = useState(false)

  // Award names, editable per the brief.
  const [awards, setAwards] = useState<Record<string, string>>({})
  const [awardBusy, setAwardBusy] = useState<string | null>(null)

  // Add-teammate form.
  const [addOpen, setAddOpen] = useState(false)
  const [addEmail, setAddEmail] = useState('')
  const [addPlayerId, setAddPlayerId] = useState('')
  const [adding, setAdding] = useState(false)

  const load = useCallback(async () => {
    if (!supabase) return
    setChecking(true)
    const [directory, rosterRes, awardRes] = await Promise.all([
      // The directory reads the auth schema (last sign-in, live sessions) behind
      // a SECURITY DEFINER function, so this one call has everything the screen
      // shows and there is no second privileged round trip.
      supabase.rpc('admin_member_directory'),
      supabase.from('players').select('id, display_name').order('display_name'),
      supabase.from('award_names').select('stat_type, label'),
    ])
    setChecking(false)
    if (directory.error) {
      setNote({ kind: 'error', text: `Could not load members: ${directory.error.message}` })
      return
    }
    setMembers((directory.data ?? []) as MemberRow[])
    setRoster((rosterRes.data ?? []) as RosterName[])
    setAwards(
      Object.fromEntries(((awardRes.data ?? []) as { stat_type: string; label: string }[]).map((a) => [a.stat_type, a.label])),
    )
    setCheckedAt(Date.now())
  }, [])

  /*
   * Polled, not loaded once. Six people claim within a few minutes of the link
   * going out, usually while the admin watches this screen; loading on mount
   * only meant a claim arriving thirty seconds later never appeared, and an
   * empty panel looked identical whether nobody had claimed or the screen had
   * stopped asking. The timestamp below says which.
   */
  useEffect(() => {
    void load()
    const id = setInterval(() => void load(), 15_000)
    return () => clearInterval(id)
  }, [load])

  const update = async (row: MemberRow, patch: Partial<MemberRow>, description: string) => {
    if (!supabase) return
    setBusy(row.id)
    setNote(null)
    const { error } = await supabase.from('team_members').update(patch).eq('id', row.id)
    setBusy(null)
    if (error) {
      setNote({ kind: 'error', text: `${description} failed: ${error.message}` })
      return
    }
    setNote({ kind: 'ok', text: description })
    await load()
  }

  const addTeammate = async () => {
    const url = fn('team-admin')
    if (!url) return
    const email = addEmail.trim().toLowerCase()
    if (!email) {
      setNote({ kind: 'error', text: 'Enter their email address.' })
      return
    }
    if (!addPlayerId) {
      setNote({ kind: 'error', text: 'Pick their name from the roster.' })
      return
    }
    setAdding(true)
    setNote(null)
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { ...(await authHeaders()), 'content-type': 'application/json' },
        body: JSON.stringify({ action: 'addTeammate', email, playerId: addPlayerId }),
      })
      const body = (await res.json().catch(() => ({}))) as { ok?: boolean; member?: { display_name?: string }; error?: string }
      if (!res.ok || !body.ok) {
        setNote({ kind: 'error', text: body.error ?? 'Could not add them. Try again.' })
        return
      }
      setNote({
        kind: 'ok',
        text: `${body.member?.display_name ?? 'Teammate'} added and approved. They sign in with their email — no code needed.`,
      })
      setAddEmail('')
      setAddPlayerId('')
      setAddOpen(false)
      await load()
    } catch {
      setNote({ kind: 'error', text: 'No connection. Try again when you are back online.' })
    } finally {
      setAdding(false)
    }
  }

  // Named sets the admin can drop in whole (03.6). D is the default and lives in
  // the database seed; these are the alternatives to pick from.
  const awardSets: { name: string; labels: Record<string, string> }[] = [
    { name: 'Firehawks (default)', labels: { reb: 'Nest Keeper', ast: 'Wingman', stl: 'Swoop' } },
    { name: 'Classic', labels: { reb: 'Glass Cleaner', ast: 'Playmaker', stl: 'Pickpocket' } },
    { name: 'Plain', labels: { reb: 'Boards', ast: 'Dimes', stl: 'Thief' } },
    { name: 'Cheeky', labels: { reb: 'Vacuum', ast: 'The Chef', stl: 'Cat Burglar' } },
  ]

  const applyAwardSet = async (labels: Record<string, string>) => {
    setAwards((a) => ({ ...a, ...labels }))
    // Persist all three so the chips are one tap, not "fill then save x3".
    for (const [stat, label] of Object.entries(labels)) await saveAward(stat, label, true)
    setNote({ kind: 'ok', text: 'Award names updated.' })
    await load()
  }

  const saveAward = async (statType: string, label: string, quiet = false) => {
    const url = fn('team-admin')
    if (!url) return
    setAwardBusy(statType)
    setNote(null)
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { ...(await authHeaders()), 'content-type': 'application/json' },
        body: JSON.stringify({ action: 'setAwardName', statType, label }),
      })
      const body = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string }
      if (!res.ok || !body.ok) {
        setNote({ kind: 'error', text: body.error ?? 'Could not rename the award.' })
        return
      }
      if (!quiet) setNote({ kind: 'ok', text: 'Award renamed.' })
    } finally {
      setAwardBusy(null)
    }
  }

  const nameOf = (row: MemberRow) => row.display_name ?? (row.player_id ? 'Unknown player' : 'No name picked')
  const isSelf = (row: MemberRow) => row.auth_user_id === session?.user.id

  const pending = useMemo(() => (members ?? []).filter((m) => m.status === 'pending'), [members])
  const active = useMemo(() => (members ?? []).filter((m) => m.status === 'approved'), [members])
  const revoked = useMemo(() => (members ?? []).filter((m) => m.status === 'revoked'), [members])

  // The roster the Add form offers: names nobody live already holds.
  const takenPlayerIds = useMemo(
    () => new Set((members ?? []).filter((m) => m.status !== 'revoked').map((m) => m.player_id)),
    [members],
  )
  const availableRoster = useMemo(
    () => roster.filter((r) => !takenPlayerIds.has(r.id)),
    [roster, takenPlayerIds],
  )

  if (members === null) {
    return (
      <div className="panel">
        <p className="muted">Loading members...</p>
      </div>
    )
  }

  return (
    <div className="manage">
      {note && <div className={`manage__note manage__note--${note.kind}`}>{note.text}</div>}

      {/* Proof the screen is still asking, so "nobody waiting" can be believed. */}
      <div className="admin__status muted small">
        <span>
          {members.length} account{members.length === 1 ? '' : 's'} on the server
          {checkedAt ? ` · checked ${agoLabel(checkedAt)}` : ''}
        </span>
        <button type="button" className="btn btn--sm" disabled={checking} onClick={() => void load()}>
          {checking ? 'Checking...' : 'Check again'}
        </button>
      </div>

      <section className="panel">
        <div className="admin__addhead">
          <h2 className="panel__title">Add a teammate</h2>
          <button type="button" className="btn btn--sm" onClick={() => setAddOpen((o) => !o)}>
            {addOpen ? 'Close' : 'Add'}
          </button>
        </div>
        {addOpen && (
          <div className="admin__add">
            <label className="field">
              <span>Their email</span>
              <input
                type="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                placeholder="teammate@example.com"
              />
            </label>
            <label className="field">
              <span>Which name on the roster</span>
              <select value={addPlayerId} onChange={(e) => setAddPlayerId(e.target.value)}>
                <option value="">Pick a name...</option>
                {availableRoster.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.display_name}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="btn btn--primary"
              disabled={adding || !addEmail.trim() || !addPlayerId}
              onClick={() => void addTeammate()}
            >
              {adding ? 'Adding...' : 'Add, already approved'}
            </button>
            <p className="muted small">
              Sets them up directly, no approval step. They sign in with their email alone — the
              team code is only for people who add themselves. Use this for anyone whose sign-in
              email is not reaching them.
            </p>
          </div>
        )}
      </section>

      <section className="panel">
        <h2 className="panel__title">Award names</h2>
        <div className="admin__award-sets">
          {awardSets.map((set) => (
            <button key={set.name} type="button" className="chip" onClick={() => void applyAwardSet(set.labels)}>
              {set.name}
            </button>
          ))}
        </div>
        <div className="admin__awards">
          {([['reb', 'Rebounds'], ['ast', 'Assists'], ['stl', 'Steals']] as const).map(([stat, what]) => (
            <div key={stat} className="admin__award">
              <label className="field">
                <span>{what}</span>
                <input
                  value={awards[stat] ?? ''}
                  onChange={(e) => setAwards((a) => ({ ...a, [stat]: e.target.value }))}
                  placeholder="Award name"
                />
              </label>
              <button
                type="button"
                className="btn btn--sm"
                disabled={awardBusy === stat || !(awards[stat] ?? '').trim()}
                onClick={() => void saveAward(stat, (awards[stat] ?? '').trim())}
              >
                {awardBusy === stat ? 'Saving...' : 'Save'}
              </button>
            </div>
          ))}
        </div>
        <p className="muted small">These name the three awards in every recap. Ties share the award.</p>
      </section>

      <ReportsPanel />

      <section className="panel">
        <h2 className="panel__title">
          Waiting for you <span className="count-badge">{pending.length}</span>
        </h2>
        {pending.length === 0 ? (
          <p className="muted">
            Nobody waiting. This list refreshes every 15 seconds, so a claim made now will appear
            without reloading. Somebody who has only been given the code is not here yet: they show
            up once they have clicked their sign-in link and picked a name.
          </p>
        ) : (
          <div className="rows">
            {pending.map((m) => (
              <div key={m.id} className="row">
                <div className="row__label">
                  <span className="row__title">{nameOf(m)}</span>
                  <span className="row__meta">
                    {m.email} · claimed {formatDate(m.claimed_at.slice(0, 10))}
                  </span>
                </div>
                <button
                  type="button"
                  className="btn btn--sm btn--primary"
                  disabled={busy === m.id}
                  onClick={() => void update(m, { status: 'approved', approved_at: new Date().toISOString() }, `${nameOf(m)} approved`)}
                >
                  Approve
                </button>
                <button
                  type="button"
                  className="btn btn--sm btn--danger"
                  disabled={busy === m.id}
                  onClick={() => void update(m, { status: 'revoked' }, `${nameOf(m)} rejected`)}
                >
                  Reject
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="panel">
        <h2 className="panel__title">
          Team <span className="count-badge">{active.length}</span>
        </h2>
        <div className="rows">
          {active.map((m) => (
            <div key={m.id} className="row">
              <div className="row__label">
                <span className="row__title">
                  {nameOf(m)}
                  {isSelf(m) && <span className="chip__tag">you</span>}
                </span>
                <span className="row__meta">
                  {m.email} · {m.role} · {signInLabel(m)}
                </span>
              </div>
              {!isSelf(m) && (
                <>
                  <button
                    type="button"
                    className="btn btn--sm"
                    disabled={busy === m.id}
                    onClick={() =>
                      void update(
                        m,
                        { role: m.role === 'admin' ? 'scorer' : 'admin' },
                        `${nameOf(m)} is now ${m.role === 'admin' ? 'a scorer' : 'an admin'}`,
                      )
                    }
                  >
                    {m.role === 'admin' ? 'Make scorer' : 'Make admin'}
                  </button>
                  <button
                    type="button"
                    className="btn btn--sm"
                    disabled={busy === m.id}
                    onClick={() =>
                      void update(
                        m,
                        { role: m.role === 'player' ? 'scorer' : 'player' },
                        `${nameOf(m)} can ${m.role === 'player' ? 'now' : 'no longer'} score`,
                      )
                    }
                  >
                    {m.role === 'player' ? 'Allow scoring' : 'Stop scoring'}
                  </button>
                  <button
                    type="button"
                    className="btn btn--sm btn--danger"
                    disabled={busy === m.id}
                    onClick={() => void update(m, { status: 'revoked' }, `${nameOf(m)} removed`)}
                  >
                    Remove
                  </button>
                </>
              )}
            </div>
          ))}
          {active.length === 0 && <p className="muted">Nobody approved yet.</p>}
        </div>
      </section>

      {revoked.length > 0 && (
        <section className="panel">
          <h2 className="panel__title">Removed</h2>
          <div className="rows">
            {revoked.map((m) => (
              <div key={m.id} className="row row--muted">
                <div className="row__label">
                  <span className="row__title">{nameOf(m)}</span>
                  <span className="row__meta">{m.email}</span>
                </div>
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={busy === m.id}
                  onClick={() => void update(m, { status: 'approved', approved_at: new Date().toISOString() }, `${nameOf(m)} restored`)}
                >
                  Restore
                </button>
              </div>
            ))}
          </div>
          <p className="muted small">
            Removed accounts keep their row, so the name is free to be claimed again and the history of
            who had access is not lost.
          </p>
        </section>
      )}
    </div>
  )
}

/** "signed in 2h ago on 2 devices" / "never signed in" -- the health of an account at a glance. */
function signInLabel(m: MemberRow): string {
  if (!m.last_sign_in_at) {
    return m.created_by_admin ? 'added by you, not signed in yet' : 'never signed in'
  }
  const when = agoLabel(Date.parse(m.last_sign_in_at))
  const devices = m.device_count === 0 ? 'no active device' : `${m.device_count} device${m.device_count === 1 ? '' : 's'}`
  return `last in ${when} · ${devices}`
}

/** "just now" / "40s ago" / "3m ago" / "5h ago" / "2d ago". */
function agoLabel(at: number): string {
  const s = Math.max(0, Math.round((Date.now() - at) / 1000))
  if (s < 5) return 'just now'
  if (s < 60) return `${s}s ago`
  if (s < 3600) return `${Math.round(s / 60)}m ago`
  if (s < 86400) return `${Math.round(s / 3600)}h ago`
  return `${Math.round(s / 86400)}d ago`
}

// --- reports -----------------------------------------------------------------

type ReportKind = 'season' | 'pregame' | 'recap'

interface RunResult {
  kind: string
  status: 'rendered' | 'sent' | 'skipped' | 'failed'
  recipients?: string[]
  subject?: string
  reason?: string
  html?: string
}

interface LogRow {
  kind: string
  for_date: string | null
  status: 'rendered' | 'sent' | 'skipped' | 'failed'
  sim: boolean
  dry_run: boolean
  resend: boolean
  subject: string | null
  error: string | null
  created_at: string
}

interface RunResponse {
  now: { melbourne: string }
  sim: boolean
  dryRun: boolean
  due: string[]
  recapGameId: string | null
  recapNote?: string
  results: RunResult[]
  log?: LogRow[]
  error?: string
}

/**
 * Preview and resend the three reports (task 04, gap 4).
 *
 * Authority is the signed-in admin's own role, like the rest of this screen: the
 * browser never holds the shared password, so run-reports grew a third door that
 * checks is_admin() from the caller's JWT.
 *
 * Preview is a DRY RUN -- it renders and returns the HTML and sends nothing, so
 * there is no way to mail the team by clicking the wrong thing. Sending is a
 * second, explicitly labelled press, and a resend is marked as one so the
 * scheduler's once-only guarantee is untouched.
 */
function ReportsPanel() {
  const [busy, setBusy] = useState<string | null>(null)
  const [run, setRun] = useState<RunResponse | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [log, setLog] = useState<LogRow[] | null>(null)

  const call = async (label: string, body: Record<string, unknown>) => {
    const url = fn('run-reports')
    if (!url) {
      setError('No backend configured, so there is nothing to run.')
      return
    }
    setBusy(label)
    setError(null)
    setPreview(null)
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { ...(await authHeaders()), 'content-type': 'application/json' },
        body: JSON.stringify(body),
      })
      const text = await res.text()
      let parsed: RunResponse | null = null
      try {
        parsed = JSON.parse(text) as RunResponse
      } catch {
        // A body that is not JSON is itself the diagnosis; show it verbatim.
        throw new Error(`${res.status} ${text.slice(0, 300)}`)
      }
      if (!res.ok) throw new Error(parsed.error ?? `${res.status}`)
      if (parsed.log) {
        setLog(parsed.log)
        return
      }
      setRun(parsed)
      const withHtml = (parsed.results ?? []).find((r) => r.html)
      if (withHtml?.html) setPreview(withHtml.html)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(null)
    }
  }

  const previewOne = (kind: ReportKind) =>
    call(`preview-${kind}`, { only: kind, force: true, dryRun: true })

  const sendOne = (kind: ReportKind) =>
    call(`send-${kind}`, { only: kind, force: true, dryRun: false, resend: true })

  return (
    <section className="panel">
      <h2 className="panel__title">Reports</h2>
      <p className="muted small">
        Preview renders the real email and sends nothing. Send mails it now, even if one has
        already gone out today — a resend is recorded as one, so tonight's scheduled report still
        goes out normally.
      </p>

      <div className="admin__reports">
        {(['season', 'pregame', 'recap'] as const).map((kind) => (
          <div key={kind} className="admin__report">
            <span className="row__title">{kind === 'season' ? 'Season so far' : kind === 'pregame' ? 'Pre-game note' : 'Game recap'}</span>
            <div className="manage__actions">
              <button type="button" className="btn btn--sm" disabled={!!busy} onClick={() => void previewOne(kind)}>
                {busy === `preview-${kind}` ? 'Rendering…' : 'Preview'}
              </button>
              <button type="button" className="btn btn--sm" disabled={!!busy} onClick={() => void sendOne(kind)}>
                {busy === `send-${kind}` ? 'Sending…' : 'Send now'}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="manage__actions">
        <button type="button" className="btn btn--sm" disabled={!!busy} onClick={() => void call('due', { dryRun: true })}>
          {busy === 'due' ? 'Checking…' : "What's due right now?"}
        </button>
        <button type="button" className="btn btn--sm" disabled={!!busy} onClick={() => void call('log', { log: true, limit: 25 })}>
          {busy === 'log' ? 'Reading…' : 'What has been sent?'}
        </button>
      </div>

      {error && <div className="manage__note manage__note--error">Could not run that: {error}</div>}

      {run && (
        <div className="admin__runinfo">
          <p className="muted small">
            Melbourne time {run.now.melbourne}
            {run.sim ? ' (simulated)' : ''} · {run.dryRun ? 'dry run, nothing sent' : 'live send'}
          </p>
          <p className="muted small">Due: {run.due.length ? run.due.join(', ') : 'nothing'}</p>
          {/* Never let "no recap" be silent: the server says which game and why. */}
          {run.recapNote && <p className="muted small">Recap: {run.recapNote}</p>}
          {run.results.map((r, i) => (
            <p key={i} className="muted small">
              {r.kind}: <strong>{r.status}</strong>
              {r.subject ? ` — ${r.subject}` : ''}
              {r.recipients?.length ? ` → ${r.recipients.join(', ')}` : ''}
              {r.reason ? ` (${r.reason})` : ''}
            </p>
          ))}
        </div>
      )}

      {/* The audit trail. A skipped row is as informative as a sent one: it is
          how you tell "the schedule ran and correctly did nothing" from "the
          schedule never ran". */}
      {log && (
        <div className="admin__runinfo">
          {log.length === 0 ? (
            <p className="muted small">Nothing has been rendered or sent yet.</p>
          ) : (
            log.map((r, i) => (
              <p key={i} className="muted small">
                {r.created_at.slice(0, 16).replace('T', ' ')} · {r.kind} ·{' '}
                <strong>{r.status}</strong>
                {r.resend ? ' (resend)' : ''}
                {r.sim ? ' (simulated clock)' : ''}
                {r.for_date ? ` · for ${r.for_date}` : ''}
                {r.error ? ` — ${r.error}` : ''}
              </p>
            ))
          )}
        </div>
      )}

      {preview && (
        <iframe
          className="admin__preview"
          title="Report preview"
          sandbox=""
          srcDoc={preview}
        />
      )}
    </section>
  )
}
